import type { CastTextEntry } from '../../state/book-configurator-store';

/**
 * Reload-trap fix (Sebastian ruling 2026-10-09 09:38) — shared
 * "person complete?" predicate used by BOTH `PersonenRoute` (to
 * decide which block to open on mount) and `PersonenFlow` (to drive
 * the per-entry Fertig button + decide whether to render the
 * AddAnotherPrompt). Centralising the predicate guarantees the two
 * callers can never disagree on "complete".
 *
 * A person is "complete" iff every REQUIRED field is answered:
 *   - name non-empty
 *   - ageYears not null
 *   - geschlecht non-empty (not the empty-string sentinel)
 *   - face photo present (keyed by castKey into `facePhotoByKey`)
 *   - species non-empty (tier branch only)
 *
 * OPTIONAL fields that do NOT gate "complete":
 *   - personenNotiz / Beziehung (round 2 Q_relation — Überspringen
 *     allowed)
 *   - role-pick evidence (if the entry has name/age/photo, a role was
 *     implied by the data at save time — we don't need a separate
 *     signal)
 *
 * The predicate is pure — it takes the entry + a bool for "face
 * photo present" and makes no store/IDB reads. Call sites pass the
 * face-photo bool in from their own state.
 */
export function isPersonComplete(
  entry: CastTextEntry,
  hasFacePhoto: boolean,
): boolean {
  if (entry.name.trim().length === 0) return false;
  if (entry.ageYears === null) return false;
  if (entry.geschlecht === '') return false;
  if (!hasFacePhoto) return false;
  if (entry.personType === 'tier' && entry.species.trim().length === 0) return false;
  return true;
}

/**
 * All persons in the cast complete? Empty cast → `false` (no persons
 * to be complete about; the Personen block still needs attention).
 */
export function allPersonsComplete(
  cast: readonly CastTextEntry[],
  facePhotoByKey: ReadonlyMap<string, boolean>,
): boolean {
  if (cast.length === 0) return false;
  return cast.every((entry) =>
    isPersonComplete(entry, facePhotoByKey.get(entry.key) ?? false),
  );
}
